import type { AABB } from './types';

export interface SpatialHash<T> {
  insert(item: T, box: AABB): void;
  query(box: AABB): readonly T[];
  readonly size: number;
}

export function createSpatialHash<T>(cellSize: number): SpatialHash<T> {
  const columns = new Map<number, Map<number, Set<T>>>();
  let itemCount = 0;

  function cellRange(box: AABB): { minCx: number; minCy: number; maxCx: number; maxCy: number } {
    return {
      minCx: Math.floor(box.minX / cellSize),
      minCy: Math.floor(box.minY / cellSize),
      maxCx: Math.floor(box.maxX / cellSize),
      maxCy: Math.floor(box.maxY / cellSize),
    };
  }

  return {
    insert(item: T, box: AABB): void {
      const { minCx, minCy, maxCx, maxCy } = cellRange(box);
      for (let cx = minCx; cx <= maxCx; cx++) {
        let column = columns.get(cx);
        if (!column) columns.set(cx, (column = new Map()));
        for (let cy = minCy; cy <= maxCy; cy++) {
          let cell = column.get(cy);
          if (!cell) {
            cell = new Set();
            column.set(cy, cell);
          }
          cell.add(item);
        }
      }
      itemCount++;
    },

    query(box: AABB): readonly T[] {
      const result = new Set<T>();
      const { minCx, minCy, maxCx, maxCy } = cellRange(box);
      for (let cx = minCx; cx <= maxCx; cx++) {
        const column = columns.get(cx);
        if (!column) continue;
        for (let cy = minCy; cy <= maxCy; cy++) {
          const cell = column.get(cy);
          if (cell) {
            for (const item of cell) {
              result.add(item);
            }
          }
        }
      }
      return [...result];
    },

    get size(): number {
      return itemCount;
    },
  };
}
